/**
 * Pixel Registry
 *
 * Central registry for managing pixel configurations and adapters.
 * Provides methods to query which pixels are active and retrieve their adapters.
 *
 * Firing Modes:
 * - browser_only: Fire only in browser (Microsoft Ads, Taboola)
 * - server_only: Fire only server-side, suppress browser pixel (admin choice)
 * - both: Fire both browser and server with event_id for deduplication (Facebook, TikTok)
 * - browser_or_server: Fire one or the other, never both (GA4 purchase)
 *
 * @since 1.51.0
 */

(function (pmw, $, undefined) {

	/**
	 * Valid firing modes
	 * @type {string[]}
	 */
	const VALID_FIRING_MODES = ["browser_only", "server_only", "both", "browser_or_server"];

	/**
	 * Default firing mode for pixels that don't specify one
	 * @type {string}
	 */
	const DEFAULT_FIRING_MODE = "browser_only";

	/**
	 * Pixel Registry namespace
	 */
	pmw.pixelRegistry = {

		/**
		 * Get all active pixels (both browser-only and S2S-capable)
		 *
		 * Queries each registered adapter's isActive function to determine
		 * if the pixel should be loaded based on pmwDataLayer configuration.
		 *
		 * @returns {string[]} Array of active pixel names
		 */
		getActivePixels() {
			const activePixels = [];

			if (!pmw.pixelCapabilities) {
				return activePixels;
			}

			// Check each registered pixel's isActive function
			for (const [pixelName, capabilities] of Object.entries(pmw.pixelCapabilities)) {
				if (typeof capabilities.isActive === "function") {
					try {
						if (capabilities.isActive()) {
							activePixels.push(pixelName);
						}
					} catch (e) {
						pmw.console.error(`Error checking if ${pixelName} is active:`, e);
					}
				}
			}

			return activePixels;
		},

		/**
		 * Get browser-only pixels (pixels that don't support S2S)
		 *
		 * @returns {string[]} Array of browser-only pixel names
		 */
		getActiveBrowserOnlyPixels() {
			const browserPixels = [];
			const activePixels  = this.getActivePixels();

			for (const pixelName of activePixels) {
				// If pixel doesn't support S2S, it's browser-only
				if (pmw.pixelCapabilities?.[pixelName]?.supportsS2S !== true) {
					browserPixels.push(pixelName);
				}
			}

			return browserPixels;
		},

		/**
		 * Get S2S-capable pixels
		 *
		 * Returns pixels that declared supportsS2S capability when registering.
		 * These pixels ALWAYS receive the pmw:s2s:* events to fire their browser pixels (fbq, ttq, etc.),
		 * regardless of whether S2S is actually enabled. They handle their own S2S logic internally.
		 *
		 * @returns {string[]} Array of S2S pixel names
		 */
		getActiveS2SPixels() {
			const s2sPixels    = [];
			const activePixels = this.getActivePixels();

			// Check each active pixel's capabilities
			for (const pixelName of activePixels) {
				// Check if this pixel declared supportsS2S capability when registering
				if (pmw.pixelCapabilities?.[pixelName]?.supportsS2S === true) {
					s2sPixels.push(pixelName);
				}
			}

			return s2sPixels;
		},

		/**
		 * Get S2S-configured pixels that are not active (browser pixel not loaded)
		 *
		 * Returns pixels where:
		 * - supportsS2S === true
		 * - isConfigured() returns true (pixel has valid config in the data layer)
		 * - isActive() returns false (browser pixel hasn't loaded, e.g. due to consent manager)
		 *
		 * Only returns results when the "Always Send Server-Side Events" setting is enabled.
		 *
		 * @returns {string[]} Array of configured-but-not-loaded S2S pixel names
		 * @since 1.57.0
		 */
		getS2SConfiguredPixels() {
			const configuredPixels = [];

			if (!pmw.pixelCapabilities) {
				return configuredPixels;
			}

			// Only return results when always_send_s2s is enabled
			if (pmwDataLayer?.general?.server_2_server?.always_send_s2s !== true) {
				return configuredPixels;
			}

			for (const [pixelName, capabilities] of Object.entries(pmw.pixelCapabilities)) {
				// Skip pixels that don't support S2S
				if (capabilities.supportsS2S !== true) {
					continue;
				}

				// Skip pixels that are already active (loaded normally with consent)
				if (typeof capabilities.isActive === "function" && capabilities.isActive()) {
					continue;
				}

				// Check if pixel has valid configuration (but browser pixel hasn't loaded)
				if (typeof capabilities.isConfigured === "function") {
					try {
						if (capabilities.isConfigured()) {
							configuredPixels.push(pixelName);
						}
					} catch (e) {
						pmw.console.error(`Error checking if ${pixelName} is configured:`, e);
					}
				}
			}

			return configuredPixels;
		},

		/**
		 * Get all processable pixels: active pixels + S2S-configured-but-not-loaded pixels
		 *
		 * This is the union of getActivePixels() and getS2SConfiguredPixels().
		 * Used by the event processor to ensure S2S-configured pixels get their
		 * adapters called even when browser consent has blocked the pixel from loading.
		 *
		 * @returns {string[]} Deduplicated array of processable pixel names
		 * @since 1.57.0
		 */
		getProcessablePixels() {
			const activePixels     = this.getActivePixels();
			const configuredPixels = this.getS2SConfiguredPixels();

			// Deduplicate (configuredPixels shouldn't overlap with activePixels, but be safe)
			const allPixels = new Set([...activePixels, ...configuredPixels]);
			return Array.from(allPixels);
		},

		/**
		 * Get adapter for a specific pixel
		 *
		 * @param {string} pixelName - The pixel name (e.g., 'facebook', 'google_ads')
		 * @returns {object|null} The adapter object or null if not found
		 */
		getAdapter(pixelName) {
			if (!pmw.adapters) {
				return null;
			}

			return pmw.adapters[pixelName] || null;
		},

		/**
		 * Get active pixels with a specific capability
		 *
		 * Returns pixels that have the specified capability flag set to true.
		 * This is a generic method that allows querying any capability
		 * without needing dedicated methods for each one.
		 *
		 * @param {string} capabilityName - The capability to check (e.g., 'supportsS2S', 'supportsPageViewS2S')
		 * @returns {string[]} Array of pixel names with the specified capability
		 *
		 * @example
		 * // Get all pixels that support page view S2S
		 * pmw.pixelRegistry.getPixelsWithCapability('supportsPageViewS2S')
		 * // Returns: ['facebook', 'snapchat', 'reddit']
		 */
		getPixelsWithCapability(capabilityName) {
			const pixelsWithCapability = [];
			const activePixels         = this.getActivePixels();

			for (const pixelName of activePixels) {
				if (pmw.pixelCapabilities?.[pixelName]?.[capabilityName] === true) {
					pixelsWithCapability.push(pixelName);
				}
			}

			return pixelsWithCapability;
		},

		/**
		 * Get the firing mode for a specific pixel
		 *
		 * Returns the registered firing mode for the pixel. The mode determines
		 * whether events fire in browser, server, or both.
		 *
		 * @param {string} pixelName - The pixel name
		 * @returns {string} The firing mode (browser_only, server_only, both, browser_or_server)
		 */
		getFiringMode(pixelName) {
			const mode = pmw.pixelCapabilities?.[pixelName]?.mode;

			// Validate mode is one of the allowed values
			if (mode && VALID_FIRING_MODES.includes(mode)) {
				return mode;
			}

			// Legacy support: if supportsS2S is true but no mode specified, default to 'both'
			if (pmw.pixelCapabilities?.[pixelName]?.supportsS2S === true) {
				return "both";
			}

			return DEFAULT_FIRING_MODE;
		},

		/**
		 * Determine whether browser and/or server should fire for a pixel/event combination
		 *
		 * This is the core method for the unified firing mode system. It considers:
		 * - The pixel's registered firing mode
		 * - Whether S2S is globally enabled
		 * - Any filters that modify firing behavior
		 *
		 * @param {string} pixelName - The pixel name
		 * @param {string} eventName - The event name (e.g., 'add_to_cart', 'purchase')
		 * @returns {object} { fire_browser: boolean, fire_server: boolean }
		 */
		getFiringDecision(pixelName, eventName) {
			const mode        = this.getFiringMode(pixelName);
			const s2sEnabled  = pmwDataLayer?.general?.server_2_server?.active === true;
			const supportsS2S = pmw.pixelCapabilities?.[pixelName]?.supportsS2S === true;

			// Check if this pixel is S2S-configured but not active (browser pixel not loaded)
			const isActive       = typeof pmw.pixelCapabilities?.[pixelName]?.isActive === "function" && pmw.pixelCapabilities[pixelName].isActive();
			const alwaysSendS2S  = pmwDataLayer?.general?.server_2_server?.always_send_s2s === true;
			const isS2SOnlyPixel = !isActive && alwaysSendS2S && supportsS2S && s2sEnabled;

			let decision = {
				fire_browser: true,
				fire_server : false,
			};

			// If the pixel is not active (browser pixel not loaded) but always_send_s2s is enabled,
			// force server-only firing regardless of the declared mode
			if (isS2SOnlyPixel) {
				decision = {fire_browser: false, fire_server: true};
			} else {
				switch (mode) {
					case "browser_only":
						decision = {fire_browser: true, fire_server: false};
						break;

					case "server_only":
						// Server only fires if S2S is enabled AND pixel supports it
						decision = {
							fire_browser: false,
							fire_server : s2sEnabled && supportsS2S,
						};
						break;

					case "both":
						// Browser always fires, server fires if enabled
						decision = {
							fire_browser: true,
							fire_server : s2sEnabled && supportsS2S,
						};
						break;

					case "browser_or_server":
						// If S2S enabled and supported, use server; otherwise browser
						if (s2sEnabled && supportsS2S) {
							decision = {fire_browser: false, fire_server: true};
						} else {
							decision = {fire_browser: true, fire_server: false};
						}
						break;
				}
			}

			// Apply filter to allow external modification of firing behavior
			// Use case: Force server-only for specific pixels, disable certain pixels per-event
			decision = pmw.hooks.applyFilters(
				"pmw_pixel_firing_decision",
				decision,
				pixelName,
				eventName,
				mode,
			);

			return decision;
		},

		/**
		 * Get all pixels that should fire in browser for a specific event
		 *
		 * @param {string} eventName - The event name
		 * @returns {string[]} Array of pixel names that should fire in browser
		 */
		getPixelsForBrowserFiring(eventName) {
			const pixels       = [];
			const activePixels = this.getProcessablePixels();

			for (const pixelName of activePixels) {
				const decision = this.getFiringDecision(pixelName, eventName);
				if (decision.fire_browser) {
					pixels.push(pixelName);
				}
			}

			return pixels;
		},

		/**
		 * Get all pixels that should fire server-side for a specific event
		 *
		 * @param {string} eventName - The event name
		 * @returns {string[]} Array of pixel names that should fire server-side
		 */
		getPixelsForServerFiring(eventName) {
			const pixels       = [];
			const activePixels = this.getProcessablePixels();

			for (const pixelName of activePixels) {
				const decision = this.getFiringDecision(pixelName, eventName);
				if (decision.fire_server) {
					pixels.push(pixelName);
				}
			}

			return pixels;
		},
	};

}(window.pmw = window.pmw || {}, jQuery));